import { z } from 'zod';
import type { SchedulingTypes } from '@/components/common/settings/custom-item-status/types';

export const datetimeOperators = z.enum(['eq', 'null', 'notnull', 'gt', 'lt', 'in']);
export const picklistOperators = z.enum(['eq', 'in', 'null', 'notnull']);
export const stringOperators = z.enum([
  'eq',
  'in',
  'ne',
  'containsIgnoreCase',
  'startsWithIgnoreCase',
]);

export type DateOperator = z.infer<typeof datetimeOperators>;
export type PicklistOperator = z.infer<typeof picklistOperators>;
export type StringOperator = z.infer<typeof stringOperators>;
export type ItemFilterOperator = DateOperator | PicklistOperator | StringOperator;

const schedulingTypes: SchedulingTypes[] = ['DONE', 'SCHEDULED', 'UNSCHEDULED'];

export const datetimeFilter = z
  .object({
    value: z
      .union([z.string(), z.array(z.string())])
      .nullable()
      .optional(),
    operator: datetimeOperators,
  })
  .optional();

export const picklistFilter = z
  .object({
    value: z
      .union([z.number(), z.array(z.number())])
      .nullable()
      .optional(),
    operator: picklistOperators,
  })
  .optional();

export const datetimeItemFilterSchema = z.object({
  createdAt: datetimeFilter,
  expectedFinishDateTime: datetimeFilter,
  expectedStartDateTime: datetimeFilter,
  latestFinishDateTime: datetimeFilter,
  doneDateTime: datetimeFilter,
  targetFinishDateTime: datetimeFilter,
  targetStartDateTime: datetimeFilter,
  updatedAt: datetimeFilter,
});

export const picklistItemFilterSchema = z.object({
  effortEstimate: picklistFilter,
  itemTypeId: picklistFilter,
  parentId: picklistFilter,
  ancestorId: picklistFilter,
  id: picklistFilter,
  planId: picklistFilter,
  statusId: picklistFilter,
  teamId: picklistFilter,
  integrationId: picklistFilter,
});

export const stringItemFilterSchema = z.object({
  'descendant.statusSchedulingType': z
    .object({
      value: z.array(z.enum(schedulingTypes)).nullable().optional(),
      operator: stringOperators,
    })
    .optional(),
  name: z
    .object({
      value: z.string().optional(),
      operator: stringOperators,
    })
    .optional(),
  quickFilters: z
    .object({
      value: z.array(z.string()).optional(),
      operator: stringOperators,
    })
    .optional(),
});

export const itemFilterSearchSchema = z.object({
  ...datetimeItemFilterSchema.shape,
  ...picklistItemFilterSchema.shape,
  ...stringItemFilterSchema.shape,
});

export const isDateTimeFilterKey = (
  key: string,
): key is keyof typeof datetimeItemFilterSchema.shape => {
  return Object.keys(datetimeItemFilterSchema.shape).includes(key);
};

export const isPicklistFilterKey = (
  key: string,
): key is keyof typeof picklistItemFilterSchema.shape => {
  return Object.keys(picklistItemFilterSchema.shape).includes(key);
};

export const isStringFilterKey = (
  key: string,
): key is keyof typeof stringItemFilterSchema.shape => {
  return Object.keys(stringItemFilterSchema.shape).includes(key);
};
